import { useCallback } from 'react';

import type { UseFormReturn } from 'react-hook-form';

import { useSubsidyItems } from './use-subsidy-items';

import type { AgreementFormSchema } from '../schemas';
import type { SubsidyFormSchema, SubsidyMutationSchema, SubsidyQuerySchema } from '../schemas/subsidy-schemas';
import { useSubsidyBulkUpdateMutation } from '../subsidies-apis';

export function useSubsidies(form: UseFormReturn<AgreementFormSchema>, oldSubsidies: SubsidyQuerySchema[]) {
  const { manageSubsidyItems, isManageSubsidyItemsPending } = useSubsidyItems(form);

  const updateMutation = useSubsidyBulkUpdateMutation();

  const manageSubsidies = useCallback(async () => {
    const subsidies = form.getValues(`subsidies`) || [];
    const updated = subsidies.filter((t) => t.id > 0);

    if (updated.length > 0) {
      await updateMutation.mutateAsync({
        items: updated.map((c) => subsidyFormToUpdate(c, form.getValues(`id`)) as SubsidyMutationSchema),
      });

      updated.forEach((s) => {
        const oldSubsidyItemsIds =
          oldSubsidies.find((o) => o.id === s.id)?.subsidy_deliverables?.map((d) => d.id) ?? [];
        manageSubsidyItems(s.id, oldSubsidyItemsIds);
      });
    }

    const latestItems = [...updated];
    form.setValue(`subsidies`, latestItems, { shouldDirty: false });
    return latestItems;
  }, [form, updateMutation, manageSubsidyItems, oldSubsidies]);

  return {
    manageSubsidies,
    isManageSubsidiesPending: updateMutation.isPending || isManageSubsidyItemsPending,
  };
}

export function subsidyFormToUpdate(subsidy: SubsidyFormSchema, agreementId: number): SubsidyMutationSchema {
  return {
    id: subsidy.id,
    agreement_id: agreementId,
    client_id: subsidy.client?.id ?? -1,
    subsidy_type: subsidy.subsidy_type?.value ?? null,
    primary_contact: subsidy.primary_contact?.map((c) => c.id) ?? null,
    addresses: subsidy.addresses?.map((a) => a.id) ?? null,
    subsidy_deliverables: (subsidy.subsidy_deliverables ?? []).map((d) => d.id),
  };
}
